import { useMemo } from 'react'
import { V2_SUBGRAPH_URLS, V3_SUBGRAPH_URLS_BACKUP } from 'config/constants/endpoints'
import { useGetAirdropDataQuery } from 'graphql/v2/generated/types-and-hooks'
import { useGetAirdropV3DataQuery } from 'graphql/v3/generated/types-and-hooks'
import useAccountActiveChain from 'hooks/useAccountActiveChain'
import { Price, Token } from '@echodex/swap-sdk-core'
import { Address } from 'viem'
import { isChainSupported } from 'utils/wagmi'

export const useGetAirdropData = () => {
    const { account, chainId } = useAccountActiveChain()
    const dayId = parseInt(`${new Date().getTime() / 1000 / (24 * 60 * 60)}`)
    const { data } = useGetAirdropDataQuery(
        { endpoint: V2_SUBGRAPH_URLS[chainId] },
        {
            address: account?.toLowerCase() as string,
            addressAndDayId: `${account?.toLowerCase()}-${dayId}`,
        },
        {
            enabled: account !== undefined,
        },
    )
    const { data: dataV3 } = useGetAirdropV3DataQuery(
        { endpoint: V3_SUBGRAPH_URLS_BACKUP[chainId] },
        {
            address: account?.toLowerCase() as string,
            addressAndDayId: `${account?.toLowerCase()}-${dayId}`,
        },
        {
            enabled: account !== undefined,
        },
    )

    const totalLiquidityValue = useMemo(() => {
        if (!data || !dataV3 || !isChainSupported(chainId)) return 0
        const v2Liquidities = data?.userLiquidities.reduce((acc, userLiquidity) => {
            const { liquidity, pair } = userLiquidity
            const { lpTokenPrice } = pair
            return acc + liquidity * lpTokenPrice
        }, 0)

        let v3Liquidities = 0
        for (let i = 0; i < dataV3?.positions.length; i++) {
            const position = dataV3?.positions[i]
            const token0 = new Token(
                chainId,
                position.token0.id as Address,
                parseInt(position.token0.decimals),
                position.token0.symbol,
                position.token0.name,
            )
            const token1 = new Token(
                chainId,
                position.token1.id as Address,
                parseInt(position.token1.decimals),
                position.token1.symbol,
                position.token1.name,
            )
            const sqrtRatioX96 = BigInt(position.pool.sqrtPrice)
            const Q192 = BigInt('6277101735386680763835789423207666416102355444464034512896')
            const token0Price = new Price(token1, token0, sqrtRatioX96 * sqrtRatioX96, Q192).toSignificant(8)
            const isValid =
                parseFloat(token0Price) >= parseFloat(position.tickUpper.price1) &&
                parseFloat(token0Price) <= parseFloat(position.tickLower.price1)
            if (!isValid) continue

            let amountToken0 = position.depositedToken0 - position.withdrawnToken0
            let amountToken1 = position.depositedToken1 - position.withdrawnToken1
            amountToken0 = amountToken0 < 0 ? 0 : amountToken0
            amountToken1 = amountToken1 < 0 ? 0 : amountToken1
            const amountUSD = amountToken0 * position.token0.derivedUSD + amountToken1 * position.token1.derivedUSD
            v3Liquidities += amountUSD
        }

        // v3Liquidities will be x2 points
        return v2Liquidities + v3Liquidities * 2
    }, [data, dataV3, chainId])

    const totalFarmingValue = data?.farmingUsers.reduce((acc, farmingUsers) => {
        const { amount, farmingPool } = farmingUsers
        const { pair } = farmingPool
        const { lpTokenPrice } = pair
        return acc + amount * lpTokenPrice
    }, 0)

    const totalSwapValue = useMemo(() => {
        const v2SwapVolume = data?.userSwapDayData?.swapVolumeUSD || 0
        const v3SwapVolume = dataV3?.userSwapDayData?.swapVolumeUSD || 0
        return parseFloat(v2SwapVolume) + parseFloat(v3SwapVolume)
    }, [data, dataV3])

    return {
        totalLiquidityValue,
        totalFarmingValue,
        totalSwapValue,
    }
}

export const calculatePoints = (dataValue: number, arrayFeature: TYPE_DATA[]) => {
    const newCards = [...arrayFeature]
    for (let i = 0; i < newCards.length; i++) {
        if (dataValue < newCards[i].condition) {
            newCards[i].yourRank = true
            break
        }
    }
    return newCards
}

export const useIntermediateCalculation = (dataValue: number | undefined, arrayFeature: TYPE_DATA[]): number => {
    if (!dataValue) return 0
    let result = 0
    const arrayCopy = calculatePoints(dataValue, arrayFeature)
    for (const item of arrayCopy) {
        if (item.yourRank) {
            result += dataValue * item.numberPoint
        }
    }
    return result
}

export const useCalculateYourPoints = () => {
    const { totalLiquidityValue, totalFarmingValue, totalSwapValue } = useGetAirdropData()
    const result1 = useIntermediateCalculation(totalLiquidityValue, ADD_POOL)
    const result2 = useIntermediateCalculation(totalFarmingValue, FARM)
    const result3 = useIntermediateCalculation(totalSwapValue, SWAP)
    return [result1, result2, result3]
}
export enum Operator {
    less,
    greater_than_equal,
}
export interface PropsCardMore {
    title: string
    operator: Operator
    quantity: number
    numberPoint: number
    description: string
}

export interface TYPE_DATA extends PropsCardMore {
    condition: number
    yourRank?: boolean
    pointBefore?: number
}

export const ADD_POOL: TYPE_DATA[] = [
    {
        title: 'Add',
        operator: Operator.less,
        quantity: 1,
        numberPoint: 1,
        pointBefore: 1,
        description: 'per day for every $ you add',
        condition: 999,
    },
    {
        title: 'Add',
        operator: Operator.greater_than_equal,
        quantity: 1,
        numberPoint: 1.2,
        pointBefore: 1.2,

        description: 'per day for every $ you add',
        condition: 4999,
    },
    {
        title: 'Add',
        operator: Operator.greater_than_equal,
        quantity: 5,
        numberPoint: 1.3,
        pointBefore: 1.3,

        description: 'per day for every $ you add',
        condition: 9999,
    },
    {
        title: 'Add',
        operator: Operator.greater_than_equal,
        quantity: 10,
        numberPoint: 1.4,
        pointBefore: 1.4,

        description: 'per day for every $ you add',
        condition: 99999,
    },
    {
        title: 'Add',
        operator: Operator.greater_than_equal,
        quantity: 100,
        numberPoint: 1.5,
        pointBefore: 1.5,
        description: 'per day for every $ you add',
        condition: 9999999999,
    },
]

export const FARM: TYPE_DATA[] = [
    {
        title: 'Farm',
        operator: Operator.less,
        quantity: 1,
        numberPoint: 0.1,
        description: 'per day for every $ you stake',
        condition: 999,
    },
    {
        title: 'Farm',
        operator: Operator.greater_than_equal,
        quantity: 1,
        numberPoint: 0.12,
        description: 'per day for every $ you stake',
        condition: 4999,
    },
    {
        title: 'Farm',
        operator: Operator.greater_than_equal,
        quantity: 5,
        numberPoint: 0.13,
        description: 'per day for every $ you stake',
        condition: 9999,
    },
    {
        title: 'Farm',
        operator: Operator.greater_than_equal,
        quantity: 10,
        numberPoint: 0.14,
        description: 'per day for every $ you stake',
        condition: 99999,
    },
    {
        title: 'Farm',
        operator: Operator.greater_than_equal,
        quantity: 100,
        numberPoint: 0.15,
        description: 'per day for every $ you stake',
        condition: 9999999999,
    },
]
export const SWAP: TYPE_DATA[] = [
    {
        title: 'Swap',
        operator: Operator.less,
        quantity: 1,
        numberPoint: 0.5,
        description: 'for every $ you swap',
        condition: 999,
    },
    {
        title: 'Swap',
        operator: Operator.greater_than_equal,
        quantity: 1,
        numberPoint: 0.6,
        description: 'for every $ you swap',
        condition: 4999,
    },
    {
        title: 'Swap',
        operator: Operator.greater_than_equal,
        quantity: 5,
        numberPoint: 0.65,
        description: 'for every $ you swap',
        condition: 9999,
    },
    {
        title: 'Swap',
        operator: Operator.greater_than_equal,
        quantity: 10,
        numberPoint: 0.7,
        description: 'for every $ you swap',
        condition: 99999,
    },
    {
        title: 'Swap',
        operator: Operator.greater_than_equal,
        quantity: 100,
        numberPoint: 0.75,
        description: 'for every $ you swap',
        condition: 9999999999,
    },
]
